import React from 'react';

import {useSnackbar} from 'notistack';

import {
    DigitalizedSignatureValues,
    EfeResponse,
    UploadDocumentServiceRequest,
} from 'commons/models';
import {useAlert, useSpinner} from 'hooks';
import {ClientService, DocumentServices} from 'services';
import {ConsigneeCatalog} from 'commons/types/Types';
import {getBase64Extension} from 'commons/utils/Functions';
import {useMenuContext} from 'context/onboarding-menu/OnboardingMenu';
import {Constants, Messages, SnackbarVariants} from 'commons/constants';
import {useRelatedPeopleContext} from 'context/related-people-flow/RelatedPeopleFlow';
import EfeModalMsg from 'components/efe-modal-msg/EfeModalMsg';
import SignatureForm from './signature-form/SignatureForm';

type Props = {
    fatherKycId: number;
    closeModal: () => void;
    loadUser: ConsigneeCatalog;
};

export default function ConsigneeSignature(props: Props) {
    const {loadUser, closeModal, fatherKycId} = props;

    const {expedientId} = useMenuContext();
    const {enqueueSnackbar} = useSnackbar();
    const {setLoadUsers} = useRelatedPeopleContext();
    const {showAlert, defaultError, genericError, showAlertError, closeAlert} = useAlert();

    const [isLoading, setIsLoading] = React.useState(false);

    async function getConsineesData() {
        setIsLoading(true);
        try {
            const response = await ClientService.getLoadUsers(fatherKycId);
            if (response && response.data && response.data.data) {
                setIsLoading(false);
                setLoadUsers([...response.data.data]);
                closeModal();
            } else {
                setIsLoading(false);
                enqueueSnackbar(Messages.LOAD_USERS_ERROR, {
                    variant: SnackbarVariants.ERROR,
                });
            }
        } catch (error) {
            setIsLoading(false);
            showAlertError(error, Messages.LOAD_USERS_ERROR);
        }
    }

    async function consigneeContractSigned() {
        setIsLoading(true);
        try {
            const response = await ClientService.consigneeContractSigned(loadUser.personKey);
            if (response && response.data && response.data.isConfirmed) {
                getConsineesData();
            } else {
                setIsLoading(false);
                enqueueSnackbar(Messages.ERROR_SAVING_INFORMATION, {
                    variant: SnackbarVariants.ERROR,
                });
            }
        } catch (error) {
            setIsLoading(false);
            enqueueSnackbar(Messages.ERROR_SAVING_INFORMATION, {
                variant: SnackbarVariants.ERROR,
            });
        }
    }

    async function sendSignature(value: DigitalizedSignatureValues) {
        setIsLoading(true);
        try {
            const fileConverted = await (await fetch(value.signature)).blob();
            const extension = getBase64Extension(value.signature);
            const fileName = Constants.DOC_FIRMA_FINAL;
            const request: UploadDocumentServiceRequest = {
                documentKey: fileName,
                expedientId: expedientId,
                file: fileConverted,
                kycId: Number(loadUser.personKey),
                filename: `${fileName}.${extension}`,
            };
            const response: EfeResponse<any> = await DocumentServices.updateDocument(request);
            if (response && response.data) {
                enqueueSnackbar(Messages.FILE_UPLOAD_OK, {
                    variant: SnackbarVariants.SUCCESS,
                });
                consigneeContractSigned();
            } else {
                setIsLoading(false);
                enqueueSnackbar(Messages.ERROR_SAVING_INFORMATION, {
                    variant: SnackbarVariants.ERROR,
                });
            }
        } catch (error) {
            setIsLoading(false);
            enqueueSnackbar(Messages.ERROR_SAVING_INFORMATION, {
                variant: SnackbarVariants.ERROR,
            });
        }
    }

    return (
        <>
            {isLoading && useSpinner(isLoading)}
            {showAlert && (
                <EfeModalMsg
                    error={genericError}
                    open={showAlert}
                    defaultError={defaultError}
                    handleAction={closeAlert}
                />
            )}
            <SignatureForm submitHandler={sendSignature} />
        </>
    );
}
